import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AuthConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<"loading" | "error" | "success">("loading");
  const [message, setMessage] = useState<string>("Confirming...");

  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams(location.search);

        // --- Case A: Team invitation redirect ---
        // FIX-R32-H-4: prefer the URL `invitation` param over any cached
        // `joinTeamToken` in localStorage. The URL reflects the invite the
        // user *just clicked*; localStorage may hold a stale token from a
        // previous abandoned signup and would otherwise win silently,
        // attaching the user to the wrong shop.
        const invitationId = params.get("invitation");
        const joinTeamToken = localStorage.getItem("joinTeamToken");
        const effectiveInvitationToken = invitationId || joinTeamToken;

        if (effectiveInvitationToken) {
          // Check if this is a PKCE return (user just confirmed email)
          const code = params.get("code");
          if (code) {
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
              console.error("PKCE exchange error during invitation flow:", error);
            }
          }

          // Check if user now has a session
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session) {
            localStorage.removeItem("joinTeamToken");
            setStatus("success");
            setMessage("Email verified. Redirecting to complete your team setup...");
            navigate(`/join-team/accept?token=${effectiveInvitationToken}`, { replace: true });
            return;
          }

          // No session — redirect to join-team for sign-in
          localStorage.removeItem("joinTeamToken");
          setStatus("success");
          setMessage("Redirecting...");
          navigate(`/join-team?token=${effectiveInvitationToken}`, { replace: true });
          return;
        }

        // --- Case B: Supabase PKCE flow (code parameter) ---
        const code = params.get("code");
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;

          // Check if there's a joinTeamToken waiting (PKCE return from invited signup)
          const joinTeamToken = localStorage.getItem("joinTeamToken");
          if (joinTeamToken) {
            localStorage.removeItem("joinTeamToken");
            setStatus("success");
            setMessage("Email verified. Redirecting to complete your team setup...");
            navigate(`/join-team/accept?token=${joinTeamToken}`, { replace: true });
            return;
          }

          setStatus("success");
          setMessage("Link verified. Redirecting...");
          navigate("/", { replace: true });
          return;
        }

        // --- Case C: Supabase token hash flow (older format) ---
        const hashParams = new URLSearchParams(location.hash.replace("#", ""));
        const access_token = hashParams.get("access_token");
        const refresh_token = hashParams.get("refresh_token");

        if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });
          if (error) throw error;

          // Check for joinTeamToken
          const joinTeamToken = localStorage.getItem("joinTeamToken");
          if (joinTeamToken) {
            localStorage.removeItem("joinTeamToken");
            setStatus("success");
            setMessage("Session established. Redirecting to complete your team setup...");
            navigate(`/join-team/accept?token=${joinTeamToken}`, { replace: true });
            return;
          }

          setStatus("success");
          setMessage("Session established. Redirecting...");
          navigate("/", { replace: true });
          return;
        }

        // --- Case D: Password recovery ---
        const type = params.get("type");
        if (type === "recovery") {
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session) {
            setStatus("success");
            setMessage("Password reset link verified. Redirecting...");
            navigate("/account-settings", { replace: true });
            return;
          }
        }

        // If we get here, we didn't recognize the link.
        // FIX-R33-LOW-2: drop any stale joinTeamToken so a subsequent attempt
        // with a fresh URL invitation param isn't poisoned by the leftover.
        try { localStorage.removeItem("joinTeamToken"); } catch { /* best-effort; ignore */ }
        setStatus("error");
        setMessage("This link is invalid or expired.");
      } catch (err: any) {
        console.error("Auth confirmation error:", err);
        try { localStorage.removeItem("joinTeamToken"); } catch { /* best-effort; ignore */ }
        setStatus("error");
        setMessage(err?.message || "Failed to confirm.");
      }
    };

    run();
  }, [location.search, location.hash, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status === "loading" && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
            {status === "success" && <CheckCircle2 className="h-5 w-5 text-green-600" />}
            {status === "error" && <XCircle className="h-5 w-5 text-destructive" />}
            OptiBay Confirmation
          </CardTitle>
          <CardDescription>
            {status === "loading" && "Processing..."}
            {status === "success" && "Confirmed"}
            {status === "error" && "There was a problem"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className={`text-sm ${
            status === "error" ? "text-destructive" : 
            status === "success" ? "text-green-600" : 
            "text-muted-foreground"
          }`}>
            {message}
          </p>
          {status === "error" && (
            <div className="mt-4 space-y-2">
              <Button onClick={() => navigate("/auth")} className="w-full">
                Return to Sign In
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
